/* eslint-disable @atlassian/tangerine/import/no-restricted-paths */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Suspense } from 'react';

import type { PreloadedQuery } from 'react-relay';
import {
	type Route,
	type RouteResource,
	type RouteResources,
	withRouter,
} from 'react-resource-router';

import { cssMap, jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';
import Spinner from '@atlaskit/spinner';
import {
	GenericErrorPage,
	type HubAppProps,
	HubBootstrapWrapper,
	type hubBootstrapWrapperQuery,
	HubErrorBoundary,
	mockHubRouteProps,
} from '@atlassian/company-hub-common';
import { HubAppShell } from '@atlassian/hub-app-shell';

import { hubBootstrapResource, hubPageResource } from '../services/resources';
import { useRelayResource } from '../services/resources/utils';
import { HubApp } from '../ui/hub';
import type { hubQuery } from '../ui/hub/__generated__/hubQuery.graphql';
import { getContentWidth, type GridAppearanceTypes, withBannerBackgroundColor } from '../utils';

import {
	HUB_CUSTOM_PAGE_ROUTE_NAME,
	HUB_CUSTOM_PAGE_ROUTE_PATH,
	HUB_PORTAL_PAGE_ROUTE_NAME,
	HUB_PORTAL_PAGE_ROUTE_PATH,
	HUB_PREVIEW_ROUTE_NAME,
	HUB_PREVIEW_ROUTE_PATH,
	HUB_ROUTE_NAME,
	HUB_ROUTE_PATH,
} from './constants';

// Override ResourceStoreContext type
declare module 'react-resource-router' {
	interface ResourceStoreContext {
		cloudId: string;
		helpCenterAri: string;
	}
}

const styles = cssMap({
	spinnerContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '100vh',
	},
	root: {
		display: 'flex',
		flexDirection: 'column',
		minHeight: '100%',
		margin: '0 auto',
		width: '100%',
	},
});

export type HubRoute = Route & {
	resources: RouteResources;
};

export const hubHomepageRoute: {
	component: (props: HubAppProps) => JSX.Element | null;
	exact: boolean;
	name: string;
	path: string;
	resources: (
		| RouteResource<PreloadedQuery<hubBootstrapWrapperQuery, Record<string, unknown>>>
		| RouteResource<PreloadedQuery<hubQuery, Record<string, unknown>>>
	)[];
} = {
	component: HubApp,
	exact: true,
	name: HUB_ROUTE_NAME,
	resources: [hubBootstrapResource, hubPageResource],
	path: HUB_ROUTE_PATH,
};

export const hubCustomPageRoute: {
	component: (props: HubAppProps) => JSX.Element | null;
	exact: boolean;
	name: string;
	path: string;
	resources: (
		| RouteResource<PreloadedQuery<hubBootstrapWrapperQuery, Record<string, unknown>>>
		| RouteResource<PreloadedQuery<hubQuery, Record<string, unknown>>>
	)[];
} = {
	component: HubApp,
	exact: true,
	name: HUB_CUSTOM_PAGE_ROUTE_NAME,
	resources: [hubBootstrapResource, hubPageResource],
	path: HUB_CUSTOM_PAGE_ROUTE_PATH,
};

const isSupportSiteHeaderlessEnabled = (): boolean =>
	typeof window !== 'undefined' &&
	new URLSearchParams(window.location.search).get('headerless') === 'true' &&
	fg('csm-support-site-headerless-query-param');

export const createHubHomepageRoute = (hubAppProps: HubAppProps): HubRoute => {
	const HubAppComponent = () => {
		return (
			<HubApp
				{...hubAppProps}
				headerless={isSupportSiteHeaderlessEnabled()}
				showPageNameAsHubDocumentTitle
			/>
		);
	};
	return {
		...hubHomepageRoute,
		component: HubAppComponent,
	};
};

export const createHubPreviewRoute = (): HubRoute => {
	const hubRoute = createHubHomepageRoute(mockHubRouteProps);

	return {
		...hubRoute,
		path: HUB_PREVIEW_ROUTE_PATH,
		name: HUB_PREVIEW_ROUTE_NAME,
	};
};

export const createHubCustomPageRoute = (hubAppProps: HubAppProps): HubRoute => {
	const HubCustomPageComponent = () => {
		return (
			<HubApp
				{...hubAppProps}
				headerless={isSupportSiteHeaderlessEnabled()}
				showPageNameAsHubDocumentTitle
			/>
		);
	};
	return {
		...hubCustomPageRoute,
		component: HubCustomPageComponent,
	};
};

export const hubPortalPageRoute: {
	component: (props: HubAppProps) => JSX.Element | null;
	exact: boolean;
	name: string;
	path: string;
	resources: (
		| RouteResource<PreloadedQuery<hubBootstrapWrapperQuery, Record<string, unknown>>>
		| RouteResource<PreloadedQuery<hubQuery, Record<string, unknown>>>
	)[];
} = {
	component: HubApp,
	exact: true,
	name: HUB_PORTAL_PAGE_ROUTE_NAME,
	resources: [hubBootstrapResource, hubPageResource],
	path: HUB_PORTAL_PAGE_ROUTE_PATH,
};

export const createHubPortalPageRoute = (hubAppProps: HubAppProps): HubRoute => {
	const HubPortalPageComponent = () => {
		return <HubApp {...hubAppProps} showPageNameAsHubDocumentTitle />;
	};
	return {
		...hubPortalPageRoute,
		component: HubPortalPageComponent,
	};
};

export const createRouteWithHubShell = (
	hubAppProps: HubAppProps,
	InnerComponent: React.ComponentType,
	path: string,
	name: string,
	resources: RouteResources,
	componentWidth: GridAppearanceTypes,
): HubRoute => {
	const Component = () => {
		const { queryReference: hubBootstrapQueryReference } = useRelayResource(hubBootstrapResource);

		if (!hubBootstrapQueryReference) {
			return null;
		}

		const InnerComponentWithRouter = withRouter(InnerComponent);
		const ComponentWithBannerBackgroundColor = withBannerBackgroundColor(InnerComponentWithRouter);

		const cloudId = hubBootstrapQueryReference.variables.cloudId;

		return (
			<UFOSegment name="hub-shell">
				<HubErrorBoundary
					shouldForceError
					id="route-with-hub-shell"
					packageName="@atlassian/hub"
					fallback={<GenericErrorPage />}
				>
					<Suspense
						fallback={
							<UFOLoadHold name="hub-shell-loader">
								<div css={styles.spinnerContainer}>
									<Spinner size={'large'} />
								</div>
							</UFOLoadHold>
						}
					>
						<HubBootstrapWrapper
							queryReference={hubBootstrapQueryReference}
							{...hubAppProps}
							cloudId={cloudId}
						>
							<HubAppShell>
								{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values*/}
								<div css={styles.root} style={{ maxWidth: getContentWidth(componentWidth) }}>
									<ComponentWithBannerBackgroundColor />
								</div>
							</HubAppShell>
						</HubBootstrapWrapper>
					</Suspense>
				</HubErrorBoundary>
			</UFOSegment>
		);
	};

	return {
		component: Component,
		exact: true,
		name,
		path,
		resources: [hubBootstrapResource, ...resources],
	};
};
